import type { QuoteDocument, OptionsDocument } from 'paintscout'

import cloneDeep from 'lodash/cloneDeep'
import { uuid } from '../../../util'
import { convertTablesToLineItem } from '../../line-items'
import { getInvoiceTerms } from '../../terms'
import { createQuote } from '../create-quote/create-quote'
import { calculate } from '../../../../calculator'
import { getQuoteOptions } from '../../util'

export function convertToInvoice(args: {
  quote: QuoteDocument
  options: OptionsDocument
  partialAmount?: number
}): QuoteDocument {
  const { quote, options, partialAmount } = args
  let parentQuote = quote

  if (partialAmount) {
    parentQuote = convertTablesToLineItem({
      quote: cloneDeep(quote),
      sections: ['bid', 'additional', 'pending', 'options'],
      priceOverride: partialAmount
    })
  }

  const quoteOptions = getQuoteOptions({ quote, options })
  const payments = partialAmount ? [] : parentQuote?.payments ?? []
  const statusValue = payments.length > 0 ? (parentQuote.totals.balance_due <= 0 ? 'paid' : 'partial') : 'draft'

  return calculate(
    {
      ...parentQuote,
      _id: uuid(),
      _rev: null,
      is_invoice: true,
      number: null,
      customTerms: null,
      terms: getInvoiceTerms({ options, quoteTerms: quote.terms }),
      parent_quote: quote._id,
      parent_quote_number: quote.number,
      child_invoice_number: null,
      child_invoice_numbers: null,
      quote_date: Date.now(),
      status: {
        value: statusValue
      },
      options: {
        ...quoteOptions,
        showPrices: partialAmount ? false : quoteOptions.showPrices,
        hideAllPrices: partialAmount ? true : quoteOptions.hideAllPrices
      },
      name: quote.name ? quote.name : '',
      sentReports: {},
      signatures: {},
      dates: {
        created: Date.now(),
        updated: Date.now(),
        client: {},
        staff: {},
        completion: quote.dates?.scheduled
      },
      presentationTemplate: null,
      urls: {},
      child_invoice: null,
      child_invoices: null,
      discounts: partialAmount ? null : parentQuote.discounts,
      discount: partialAmount ? null : parentQuote.discount,
      discount_description: partialAmount ? null : parentQuote.discount_description,
      discount_isShown: partialAmount ? false : parentQuote.discount_isShown,
      payments,
      tax_rate: partialAmount ? null : parentQuote.tax_rate,
      tax_description: partialAmount ? null : parentQuote.tax_description,
      tax_details: partialAmount ? [] : parentQuote.tax_details,
      assignedUsers: {
        ...(parentQuote?.owner?.id && {
          [parentQuote.owner.id]: { assigned: true }
        })
      },
      lastViewedActivity: null,
      totalAdjustment: partialAmount ? null : parentQuote.totalAdjustment,
      depositInfo: null,
      copyOf: null,
      followUps: {}
    },
    options
  )
}

export function convertToEmptyInvoice(args: { quote: QuoteDocument; options: OptionsDocument }): QuoteDocument {
  const { quote, options } = args
  const {
    type,
    owner,
    contact,
    estimatorNotes,
    client_note,
    internal_note,
    job_identifier,
    jobAddress,
    source,
    sources
  } = quote

  const created = createQuote({
    options,
    quoteType: type,
    owner,
    contact,
    invoice: true,
    type: 'quote',
    estimatorNotes,
    clientNote: client_note,
    crewNote: internal_note,
    jobIdentifier: job_identifier,
    jobAddress,
    source,
    sources
  })

  return {
    ...created,
    parent_quote_number: quote.number,
    parent_quote: quote._id,
    contact: {
      ...created.contact,
      wisetackLoanApplication: quote.contact?.wisetackLoanApplication
    }
  }
}
